import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { fetchWrapper } from '../../utils/apiHandlers'
import Button from '../Button'
import SmallButton from '../SmallButton'
import InputComp from '../InputComp'
import MessageModal from '../MessageModal'
import ConfirmModal from '../ConfirmModal'
import LoadingComponent from '../LoadingComponent'
import theme from '../../theme'

// Mirrors express-server/src/utils/directoryCategories.ts.
const CATEGORIES = [
  ['restaurants', 'Restaurants'], ['cafes', 'Cafes & Coffee'], ['bakeries', 'Bakeries & Desserts'],
  ['grocery', 'Grocery & Markets'], ['clothing', 'Clothing & Boutiques'], ['beauty', 'Beauty & Hair Salons'],
  ['gifts', 'Gifts & Flowers'], ['electronics', 'Electronics & Phones'], ['home', 'Home & Furniture'], ['pets', 'Pets & Vets'],
  ['health', 'Health & Dental'], ['fitness', 'Fitness & Wellness'],
  ['auto', 'Auto & Repair'], ['homeservices', 'Home Services'],
]

const STATUS_COLOR: { [k: string]: string } = {
  discovered: theme.colors.textMuted,
  queued: theme.colors.primary,
  crawling: theme.colors.primary,
  ready: theme.colors.success,
  error: theme.colors.danger,
}

interface CityInput { city: string; state: string; zip: string }

const parseCityInput = (raw: string): CityInput | null => {
  // "Austin, TX 78704" - loose on spacing/case, just needs a 2-letter state and a 5-digit zip somewhere.
  let zip = (/\b\d{5}\b/.exec(raw) || [''])[0]
  let state = (/\b([A-Za-z]{2})\b/.exec(raw.replace(zip, '')) || ['', ''])[1].toUpperCase()
  let city = raw.split(',')[0].trim()
  if (!city || !state || !zip) return null
  return { city, state, zip }
}

const DirectoryComp: React.FC = () => {
  const [category, setcategory] = useState('restaurants')
  const [leadin, setleadin] = useState('model')
  const [provider, setprovider] = useState('google')
  const [location, setlocation] = useState('')
  const [radiuskm, setradiuskm] = useState('8')
  const [limit, setlimit] = useState('10')
  const [includechains, setincludechains] = useState(false)

  const [bulkCityInput, setbulkCityInput] = useState('')
  const [bulkCities, setbulkCities] = useState<CityInput[]>([])
  const [bulkCategories, setbulkCategories] = useState<Set<string>>(new Set())
  const [bulkTarget, setbulkTarget] = useState('5')
  const [bulkAutopublish, setbulkAutopublish] = useState(true)
  const [coverage, setcoverage] = useState<{ [key: string]: number }>({})
  const [bulkRunning, setbulkRunning] = useState(false)

  const [filtercategory, setfiltercategory] = useState('')
  const [filterstatus, setfilterstatus] = useState('')
  const [businesses, setbusinesses] = useState<any[]>([])
  const [selected, setselected] = useState<Set<string>>(new Set())
  const [editing, setediting] = useState<string>('')

  const [loading, setloading] = useState(false)
  const [loadingmessage, setloadingmessage] = useState('')
  const [modalstate, setmodalstate] = useState(false)
  const [modalheader, setmodalheader] = useState('')
  const [modalmessage, setmodalmessage] = useState('')
  const [confirmstate, setconfirmstate] = useState(false)
  const [confirmaction, setconfirmaction] = useState<{ header: string; message: string; run: () => void }>({ header: '', message: '', run: () => {} })

  const openModal = (header: string, message: string) => {
    setmodalheader(header)
    setmodalmessage(message)
    setmodalstate(true)
  }

  const load = useCallback(async () => {
    try {
      let response = await fetchWrapper.post('/api/directory/admin/list', { category: filtercategory, status: filterstatus })
      setbusinesses(response.businesses || [])
    } catch (error) {
      openModal('Error', `${error}`)
    }
  }, [filtercategory, filterstatus])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    fetchWrapper.post('/api/directory/admin/settings/get', {})
      .then((r: any) => { if (r.settings && r.settings.leadin_mode) setleadin(r.settings.leadin_mode) })
      .catch(() => {})
  }, [])

  const changeLeadin = async (value: string) => {
    setleadin(value)
    try {
      let r: any = await fetchWrapper.post('/api/directory/admin/settings/set', { key: 'leadin_mode', value })
      if (r.status !== 200) openModal('Warning', r.msg)
    } catch (error) {
      openModal('Error', `${error}`)
    }
  }

  // While anything is queued/crawling, keep refreshing so progress shows up without a manual reload.
  const busy = businesses.some((b) => b.status === 'queued' || b.status === 'crawling')
  useEffect(() => {
    if (!busy) return
    let timer = setInterval(load, 5000)
    return () => clearInterval(timer)
  }, [busy, load])

  const discover = async () => {
    if (!location.trim()) {
      openModal('Warning', 'Enter a city or zip code.')
      return
    }
    setloading(true)
    setloadingmessage(provider === 'google' ? 'Searching Google Places...' : 'Searching OpenStreetMap - this can take up to a minute...')
    try {
      let response = await fetchWrapper.post('/api/directory/admin/discover', {
        category, location, provider, radiuskm: Number(radiuskm), limit: Number(limit), includechains,
      })
      openModal(response.status === 200 ? 'Done' : 'Warning', response.msg)
      await load()
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const addBulkCity = () => {
    let parsed = parseCityInput(bulkCityInput)
    if (!parsed) {
      openModal('Warning', 'Format: City, ST 12345 (needs a 2-letter state and a 5-digit zip).')
      return
    }
    setbulkCities((prev) => (prev.some((c) => c.zip === parsed!.zip) ? prev : [...prev, parsed!]))
    setbulkCityInput('')
  }

  const removeBulkCity = (zip: string) => setbulkCities((prev) => prev.filter((c) => c.zip !== zip))

  const toggleBulkCategory = (key: string) => {
    setbulkCategories((prev) => {
      let next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  const loadCoverage = async () => {
    if (!bulkCities.length) return
    try {
      let response = await fetchWrapper.post('/api/directory/admin/coverage', {
        cities: bulkCities, categories: bulkCategories.size ? Array.from(bulkCategories) : undefined,
      })
      if (response.status === 200) setcoverage(response.coverage || {})
    } catch (error) {
      openModal('Error', `${error}`)
    }
  }

  useEffect(() => { loadCoverage() }, [bulkCities])

  const runBulkDiscover = async () => {
    if (!bulkCities.length) return openModal('Warning', 'Add at least one city.')
    if (!bulkCategories.size) return openModal('Warning', 'Pick at least one category.')
    setbulkRunning(true)
    setloadingmessage(`Discovering across ${bulkCities.length} cities x ${bulkCategories.size} categories - this can take several minutes...`)
    try {
      let response = await fetchWrapper.post('/api/directory/admin/bulkdiscover', {
        cities: bulkCities, categories: Array.from(bulkCategories), target: Number(bulkTarget) || 5,
        autopublish: bulkAutopublish, provider,
      })
      openModal(response.status === 200 ? 'Done' : 'Warning', response.msg)
      if (response.coverage) setcoverage(response.coverage)
      await load()
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setbulkRunning(false)
  }

  const selectedIds = Array.from(selected)

  const toggle = (id: string) => {
    setselected((prev) => {
      let next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setselected(selected.size === businesses.length ? new Set() : new Set(businesses.map((b) => b._id)))
  }

  const call = async (path: string, body: any, then?: (r: any) => void) => {
    setloading(true)
    setloadingmessage('Working...')
    try {
      let response = await fetchWrapper.post(`/api/directory/admin/${path}`, body)
      if (then) then(response)
      else if (response.status !== 200) openModal('Warning', response.msg)
      await load()
    } catch (error) {
      openModal('Error', `${error}`)
    }
    setloading(false)
  }

  const enrichSelected = () => {
    if (!selectedIds.length) return openModal('Warning', 'Select businesses first.')
    call('enrich', { ids: selectedIds }, (r) => openModal(r.status === 200 ? 'Started' : 'Warning', `${r.msg} Refresh in a minute to see photos and ratings.`))
  }

  const factsSelected = () => {
    if (!selectedIds.length) return openModal('Warning', 'Select businesses first.')
    call('facts', { ids: selectedIds }, (r) => openModal(r.status === 200 ? 'Started' : 'Warning', `${r.msg} Refresh in a minute.`))
  }

  const crawlSelected = () => {
    if (!selectedIds.length) return openModal('Warning', 'Select businesses first.')
    call('crawl', { ids: selectedIds }, (r) => openModal(r.status === 200 ? 'Queued' : 'Warning', r.msg))
  }

  const publishSelected = (published: boolean) => {
    if (!selectedIds.length) return openModal('Warning', 'Select businesses first.')
    call('publish', { ids: selectedIds, published }, (r) => openModal('Done', `${r.msg}${published ? ' (only crawled businesses can be published)' : ''}`))
  }

  const askConfirm = (header: string, message: string, run: () => void) => {
    setconfirmaction({ header, message, run })
    setconfirmstate(true)
  }

  const emailSelected = () => {
    if (!selectedIds.length) return openModal('Warning', 'Select businesses first.')
    askConfirm('Send outreach emails', `Email ${selectedIds.length} selected businesses? Only crawled ones with a valid email that haven't been emailed yet will be sent. This cannot be undone.`, () => {
      setconfirmstate(false)
      call('sendoutreach', { ids: selectedIds }, (r) => {
        let failed = (r.results || []).filter((x: any) => !x.ok).map((x: any) => `${x.name}: ${x.msg}`)
        openModal('Outreach', `${r.msg}${failed.length ? `\n\nSkipped:\n${failed.join('\n')}` : ''}`)
      })
    })
  }

  const remove = (b: any) => {
    askConfirm('Delete', `Delete ${b.name} and its crawled content?`, () => {
      setconfirmstate(false)
      call('delete', { id: b._id })
    })
  }

  const saveEdit = async (b: any) => {
    await call('update', {
      id: b._id, name: b.name, email: b.email, phone: b.phone, address: b.address,
      city: b.city, state: b.state, zip: b.zip, websiteurl: b.websiteurl, description: b.description,
    }, (r) => {
      if (r.status !== 200) openModal('Warning', r.msg)
      else setediting('')
    })
  }

  const setField = (id: string, field: string, value: string) => {
    setbusinesses((prev) => prev.map((b) => (b._id === id ? { ...b, [field]: value } : b)))
  }

  return (
    <MainContainer>
      <MessageModal state={modalstate} header={modalheader} message={modalmessage} handleClose={() => setmodalstate(false)} />
      <ConfirmModal state={confirmstate} func={confirmaction.run} header={confirmaction.header} message={confirmaction.message} handleClose={() => setconfirmstate(false)} />

      <SectionTitle>Chat settings</SectionTitle>
      <SectionHint>
        Lead-in style is the friendly opening sentence of a suggestion. "Model-written" lets the AI write it (checked for
        made-up details, replaced by a template if it fails); "Template" uses fixed, reviewed sentences and is faster. Applies immediately.
      </SectionHint>
      <FormRow>
        <Select value={leadin} onChange={(e) => changeLeadin(e.target.value)}>
          <option value="model">Lead-in: model-written</option>
          <option value="template">Lead-in: template</option>
        </Select>
      </FormRow>

      <SectionTitle>Bulk seed multiple cities</SectionTitle>
      <SectionHint>
        Discover several cities x categories in one pass (what tools/seed_places.py did from the command line).
        First pass at 8 results per cell, then a second pass at 15 for any cell still short of the target.
      </SectionHint>
      <FormRow>
        <InputComp id="bulk_city_input" value={bulkCityInput} onChange={(e: any) => setbulkCityInput(e.target.value)} placeholder="City, ST 12345" width={220} />
        <SmallButton text="Add city" handleClick={addBulkCity} />
      </FormRow>
      {bulkCities.length > 0 && (
        <ChipRow>
          {bulkCities.map((c) => (
            <Chip key={c.zip}>{c.city}, {c.state} {c.zip} <ChipX onClick={() => removeBulkCity(c.zip)}>×</ChipX></Chip>
          ))}
        </ChipRow>
      )}
      <ChipRow>
        {CATEGORIES.map(([k, l]) => (
          <CategoryChip key={k} $active={bulkCategories.has(k)} onClick={() => toggleBulkCategory(k)}>{l}</CategoryChip>
        ))}
      </ChipRow>
      <FormRow>
        <label style={{ fontSize: 12 }}>Target per category</label>
        <InputComp id="bulk_target" value={bulkTarget} onChange={(e: any) => setbulkTarget(e.target.value)} placeholder="5" width={70} />
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={bulkAutopublish} onChange={(e) => setbulkAutopublish(e.target.checked)} /> autopublish
        </label>
        <Button text={bulkRunning ? 'Running…' : 'Run discovery'} handleClick={runBulkDiscover} />
      </FormRow>

      {bulkCities.length > 0 && bulkCategories.size > 0 && (
        <CoverageTable>
          <thead>
            <tr>
              <th></th>
              {Array.from(bulkCategories).map((k) => <th key={k}>{CATEGORIES.find((c) => c[0] === k)?.[1] || k}</th>)}
            </tr>
          </thead>
          <tbody>
            {bulkCities.map((c) => (
              <tr key={c.zip}>
                <td>{c.city}</td>
                {Array.from(bulkCategories).map((k) => {
                  let n = coverage[`${c.city}|${k}`] ?? 0
                  return <td key={k}><CoverageCell $ok={n >= (Number(bulkTarget) || 5)}>{n}</CoverageCell></td>
                })}
              </tr>
            ))}
          </tbody>
        </CoverageTable>
      )}

      {bulkRunning && (
        <LoadingRow>
          <LoadingComponent />
          <div>{loadingmessage}</div>
        </LoadingRow>
      )}

      <SectionTitle>1. Find businesses</SectionTitle>
      <SectionHint>
        Finds businesses in a category near a city or zip that list a website. OpenStreetMap is free but its
        public server is often busy and coverage is patchy; Google Places has better coverage but is billed per
        search and needs a server-usable key (see GOOGLE_PLACES_API_KEY). Chains are skipped unless you tick the box
        (radius only applies to OpenStreetMap). Nothing is crawled or public yet.
      </SectionHint>
      <FormRow>
        <Select value={category} onChange={(e) => setcategory(e.target.value)}>
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Select>
        <Select value={provider} onChange={(e) => setprovider(e.target.value)}>
          <option value="google">Google Places</option>
          <option value="osm">OpenStreetMap (free)</option>
        </Select>
        <InputComp id="dir_location" value={location} onChange={(e: any) => setlocation(e.target.value)} placeholder="City or zip (e.g. Miami, FL)" width={260} />
        <InputComp id="dir_radius" value={radiuskm} onChange={(e: any) => setradiuskm(e.target.value)} placeholder="Radius km" width={110} />
        <InputComp id="dir_limit" value={limit} onChange={(e: any) => setlimit(e.target.value)} placeholder="How many" width={110} />
      </FormRow>
      <FormRow>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={includechains} onChange={(e) => setincludechains(e.target.checked)} /> include chains
        </label>
        <Button text="Find businesses" handleClick={discover} />
      </FormRow>

      {loading && (
        <LoadingRow>
          <LoadingComponent />
          <div>{loadingmessage}</div>
        </LoadingRow>
      )}

      <SectionTitle>2. Crawl, review, publish</SectionTitle>
      <SectionHint>
        Crawling reads each website and builds its chatbot content (one at a time; a couple of minutes each).
        Publishing makes a crawled business visible in the public directory and shopping chat.
        Emailing sends the listing + demo link; opening that link marks the business as clicked (no crawl).
      </SectionHint>
      <FormRow>
        <Select value={filtercategory} onChange={(e) => setfiltercategory(e.target.value)}>
          <option value="">all categories</option>
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Select>
        <Select value={filterstatus} onChange={(e) => setfilterstatus(e.target.value)}>
          <option value="">all statuses</option>
          {Object.keys(STATUS_COLOR).map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <SmallButton text="Refresh" handleClick={load} />
      </FormRow>
      <FormRow>
        <SmallButton text={`Crawl selected (${selectedIds.length})`} handleClick={crawlSelected} />
        <SmallButton text="Rebuild facts" handleClick={factsSelected} backgroundColor={theme.colors.textMuted} />
        <SmallButton text="Photos & info" handleClick={enrichSelected} backgroundColor={theme.colors.accent} />
        <SmallButton text="Publish selected" handleClick={() => publishSelected(true)} backgroundColor={theme.colors.success} />
        <SmallButton text="Unpublish" handleClick={() => publishSelected(false)} backgroundColor={theme.colors.textMuted} />
        <SmallButton text="Email selected" handleClick={emailSelected} backgroundColor={theme.colors.primaryDark} />
      </FormRow>

      <HeaderRow>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={businesses.length > 0 && selected.size === businesses.length} onChange={toggleAll} /> select all ({businesses.length})
        </label>
      </HeaderRow>

      {businesses.map((b) => (
        <BusinessRow key={b._id}>
          <RowTop>
            <input type="checkbox" checked={selected.has(b._id)} onChange={() => toggle(b._id)} />
            {b.images && b.images[0] ? <Thumb src={`/api/shop-images/${encodeURIComponent(b.images[0])}`} alt="" /> : <ThumbEmpty />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <BizName>{b.name}</BizName>
              <BizMeta>
                {b.categorylabel || b.category} · {[b.city, b.state].filter(Boolean).join(', ')} ·{' '}
                <a href={b.websiteurl} target="_blank" rel="noreferrer">{b.websiteurl}</a>
              </BizMeta>
              <BizMeta>
                {b.email ? b.email : <i>no email yet</i>}
                {b.chunkcount ? ` · ${b.chunkcount} content chunks` : ''}
                {typeof b.rating === 'number' ? ` · ★ ${b.rating} (${b.ratingcount})` : ''}
                {b.images ? ` · ${b.images.length} photos` : ''}
                {b.facts && b.facts.attrs ? ` · ${Object.keys(b.facts.attrs).length} facts` : ''}
                {typeof b.pricelevel === 'number' ? ` · ${'$'.repeat(Math.max(b.pricelevel, 1))}` : ''}
              </BizMeta>
              {b.crawlerror && <ErrorText>{b.crawlerror}</ErrorText>}
            </div>
            <Badges>
              <Badge style={{ background: STATUS_COLOR[b.status] || theme.colors.textMuted }}>{b.status}</Badge>
              {b.published && <Badge style={{ background: theme.colors.success }}>public</Badge>}
              {b.outreachsentat && <Badge style={{ background: theme.colors.primaryDark }}>emailed</Badge>}
              {b.outreachclicked && <Badge style={{ background: theme.colors.accent }}>clicked</Badge>}
            </Badges>
            <SmallButton text={editing === b._id ? 'Close' : 'Edit'} handleClick={() => setediting(editing === b._id ? '' : b._id)} backgroundColor={theme.colors.textMuted} />
            <SmallButton text="Delete" handleClick={() => remove(b)} backgroundColor={theme.colors.danger} />
          </RowTop>

          {editing === b._id && (
            <EditPanel>
              <FormRow>
                <InputComp id={`n_${b._id}`} value={b.name} onChange={(e: any) => setField(b._id, 'name', e.target.value)} placeholder="Name" width={240} />
                <InputComp id={`e_${b._id}`} value={b.email} onChange={(e: any) => setField(b._id, 'email', e.target.value)} placeholder="Contact email" width={240} />
                <InputComp id={`p_${b._id}`} value={b.phone} onChange={(e: any) => setField(b._id, 'phone', e.target.value)} placeholder="Phone" width={160} />
              </FormRow>
              <FormRow>
                <InputComp id={`a_${b._id}`} value={b.address} onChange={(e: any) => setField(b._id, 'address', e.target.value)} placeholder="Street address" width={260} />
                <InputComp id={`c_${b._id}`} value={b.city} onChange={(e: any) => setField(b._id, 'city', e.target.value)} placeholder="City" width={160} />
                <InputComp id={`s_${b._id}`} value={b.state} onChange={(e: any) => setField(b._id, 'state', e.target.value)} placeholder="State" width={80} />
                <InputComp id={`z_${b._id}`} value={b.zip} onChange={(e: any) => setField(b._id, 'zip', e.target.value)} placeholder="Zip" width={100} />
              </FormRow>
              <TextArea value={b.description} onChange={(e) => setField(b._id, 'description', e.target.value)} placeholder="Short public description" />
              <FormRow>
                <SmallButton text="Save" handleClick={() => saveEdit(b)} />
              </FormRow>
            </EditPanel>
          )}
        </BusinessRow>
      ))}
    </MainContainer>
  )
}

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  gap: 8px;
  color: ${theme.colors.text};
`

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-top: 20px;
`

const SectionHint = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  margin-bottom: 4px;
`

const FormRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`

const HeaderRow = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid ${theme.colors.border};
`

const Select = styled.select`
  height: 50px;
  padding: 0 10px;
  margin: 10px;
  background: #eee;
  border: none;
  border-bottom: 1px solid black;
  font-size: 14px;
`

const ChipRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const Chip = styled.span`
  background: ${theme.colors.primarySoft};
  color: ${theme.colors.primaryDark};
  border-radius: 999px;
  padding: 4px 6px 4px 12px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const ChipX = styled.span`
  cursor: pointer;
  padding: 0 4px;
`

const CategoryChip = styled.span<{ $active: boolean }>`
  background: ${(p) => (p.$active ? theme.colors.primary : theme.colors.background)};
  color: ${(p) => (p.$active ? '#fff' : theme.colors.textMuted)};
  border: 1px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
`

const CoverageTable = styled.table`
  border-collapse: collapse;
  font-size: 11px;
  th, td {
    padding: 5px 10px;
    text-align: left;
    color: ${theme.colors.textMuted};
  }
  th {
    font-weight: 700;
  }
`

const CoverageCell = styled.span<{ $ok: boolean }>`
  background: ${(p) => (p.$ok ? '#E7F6ED' : '#FDECEF')};
  color: ${(p) => (p.$ok ? theme.colors.success : theme.colors.danger)};
  border-radius: 6px;
  padding: 2px 8px;
  font-weight: 700;
`

const LoadingRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 13px;
  color: ${theme.colors.textMuted};
`

const BusinessRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadow.card};
  padding: 12px 14px;
`

const RowTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`

const BizName = styled.div`
  font-weight: 700;
  font-size: 15px;
`

const BizMeta = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  word-break: break-all;
`

const ErrorText = styled.div`
  font-size: 12px;
  color: ${theme.colors.danger};
`

const Thumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: ${theme.radius.sm};
`

const ThumbEmpty = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.radius.sm};
  background: ${theme.colors.primarySoft};
`

const Badges = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`

const Badge = styled.span`
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
`

const EditPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid ${theme.colors.border};
  padding-top: 8px;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px;
  border-radius: ${theme.radius.sm};
  border: 1px solid ${theme.colors.border};
  font-family: inherit;
  font-size: 13px;
  resize: vertical;
`

export default DirectoryComp
